<template>
  <div class="mg-table" :class="tableLineClass">
    <div ref="topbar" class="mg-table__topbar">
      <div class="topbar-left">
        <strong v-if="showTitle">{{ title }}</strong>
        <slot name="topleft" />
      </div>
      <div class="topbar-right">
        <slot name="topright" />
        <template v-if="searchable">
          <el-button
            :class="{ 'is-active': showSearchBar }"
            icon="iconfont mg-icon_sousuo_wubiankuang"
            @click="handleToggleSearchBar"
            :disabled="false"
            type="icon"
          />
        </template>
        <el-button
          class="mg-setting-icon-btn"
          type="icon"
          @click="handleShowColumnConfig"
          v-if="configurable"
        >
          <span class="mg-setting-icon-img" />
        </el-button>
        <mg-mini-pagination
          v-if="showMiniPagination"
          :page="page"
          :total-page="totalPage"
          :total="total"
          @page-change="handleCurrentChange"
        />
      </div>
    </div>
    <el-table
      ref="mgTable"
      :border="resolvedTableBorder"
      v-loading="loading"
      :empty-text="$MGUI.i18n.t(emptyText)"
      :data="list"
      :height="height"
      :row-class-name="getRowClassName"
      :header-cell-class-name="headerCellClassName"
      :summary-method="handleSummary"
      @row-click="handleRowClick"
      @header-dragend="handleHeaderDragEnd"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <!-- 选择列 -->
      <slot name="selection" />

      <!-- 空数据是显示的内容 -->
      <div slot="empty" v-if="$slots.empty">
        <slot name="empty" />
      </div>

      <!-- 动态生成列 -->
      <template v-for="column in renderVisibleColumns">
        <!-- 使用原始的 el-table-column 渲染 -->
        <template v-if="column.default && !column.hidden">
          <slot :name="column.prop" v-bind="column" />
        </template>

        <!-- 表头分组（适用于常规列，不适于自定义的列） -->
        <mgTableColumn
          v-else-if="column.children && column.children.length"
          :key="column.prop || column.type"
          :column="column"
        >
          <template v-for="item in column.children" #[item.slot]="scope">
            <slot :name="item.slot" v-bind="scope"></slot>
          </template>
        </mgTableColumn>

        <!-- 自定义列 -->
        <el-table-column
          v-else-if="column.slot && !column.hidden"
          :key="column.prop"
          :label="column.label"
          :width="column.width"
          :min-width="column.minWidth"
          :align="column.align"
          :header-align="column.headerAlign"
          :prop="column.prop"
          :sortable="column.sortable"
          :sort-by="column.sortBy"
          :fixed="column.fixed"
          :formatter="column.formatter"
          :show-overflow-tooltip="column.showOverflowTooltip ?? true"
          :class-name="getColumnClassName(column)"
        >
          <!-- 自定义列内容 -->
          <template #default="scoped">
            <slot :name="column.slot" v-bind="scoped" />
          </template>
          <!-- 自定义表头 -->
          <template #header v-if="column.headerModel">
            <div class="mg-custom-thead-label" :class="{ 'show-bottom-line': showSearchBar }">{{ column.label }}</div>
            <div @click.stop class="mg-custom-thead-content" v-show="showSearchBar">
              <component
                v-if="column.prop"
                :is="column.headerComp || 'ElInput'"
                size="mini"
                :disabled="false"
                v-model="searchParams[column.headerModel]"
                @change="handleSearch"
                v-bind="column.headerAttrs"
              >
                <template v-if="column.headerComp === 'ElSelect'">
                  <el-option v-for="item in column.headerAttrs.options" :key="item.key || item.value" v-bind="item" />
                </template>
              </component>
              <div v-else-if="column.type === 'index'" class="mg-header-search-reset-wrap">
                <el-button
                  :disabled="false"
                  type="text"
                  class="mg-header-search-reset-btn"
                  size="mini"
                  @click="handleReset"
                >
                  {{ $MGUI.i18n.t('重置') }}
                </el-button>
              </div>
            </div>
          </template>
        </el-table-column>
        <!-- 常规列 -->
        <el-table-column
          v-else-if="!column.hidden"
          :key="column.prop || column.type"
          v-bind="{ showOverflowTooltip: true, ...column }"
          :index="getIndex"
          :class-name="getColumnClassName(column)"
        >
          <!-- 自定义表头 -->
          <template #header v-if="column.headerModel">
            <div class="mg-custom-thead-label" :class="{ 'show-bottom-line': showSearchBar }">{{ column.label }}</div>
            <div @click.stop class="mg-custom-thead-content" v-show="showSearchBar">
              <component
                v-if="column.prop"
                :is="column.headerComp || 'ElInput'"
                size="mini"
                :disabled="false"
                v-model="searchParams[column.headerModel]"
                @change="handleSearch"
                v-bind="column.headerAttrs"
              >
                <template v-if="column.headerComp === 'ElSelect'">
                  <el-option v-for="item in column.headerAttrs.options" :key="item.key || item.value" v-bind="item" />
                </template>
              </component>
              <div v-else-if="column.type === 'index'" class="mg-header-search-reset-wrap">
                <el-button
                  :disabled="false"
                  type="text"
                  class="mg-header-search-reset-btn"
                  size="mini"
                  @click="handleReset"
                >
                  {{ $MGUI.i18n.t('重置') }}
                </el-button>
              </div>
            </div>
          </template>
        </el-table-column>
      </template>
      <slot />
    </el-table>
    <el-pagination
      class="mg-pagination"
      ref="pagination"
      v-if="showPagination"
      background
      :current-page="page"
      layout="total, sizes, prev, pager, next, jumper"
      :page-size="pagination.size"
      :total="total"
      @current-change="handleCurrentChange"
      @size-change="handleSizeChange"
    />
    <mg-column-config
      v-if="configurable"
      :visible.sync="visible"
      :business-type="businessType"
      :columns="renderVisibleColumns"
      @column-change="handleColumnChange"
    />
  </div>
</template>

<script>
  import ColumnConfig from '../mixins/column-config'
  import mgMiniPagination from '../components/mg-mini-pagination'
  import mgTableColumn from '../components/mg-table-column'
  import mgColumnConfig from '../components/mg-column-config'
  export default {
    name: 'MgTable',
    props: {
      title: String,
      emptyText: {
        type: String,
        default: '暂无数据',
      },
      columns: {
        type: Array,
        default: () => [],
      },
      // 是否启用搜索
      searchable: {
        type: Boolean,
        default: false,
      },
      // 是否需要显示分页
      showPagination: {
        type: Boolean,
        default: false,
      },
      // 是否需要mini显示分页
      showMiniPagination: {
        type: Boolean,
        default: true,
      },
      // 是否可配置
      configurable: {
        type: Boolean,
        default: true,
      },
      api: Function,
      apiParams: Object,
      autoLoad: Boolean,
      // 对接口返回的数据进行格式化
      formatter: Function,
      // 合计列配置
      summaryOption: Object,
      // 表格线条模式：horizontal=仅横线，full=完整边框
      tableLineMode: {
        type: String,
        default: 'horizontal',
        validator(value) {
          return ['horizontal', 'full'].includes(value)
        },
      },
    },
    mixins: [ColumnConfig],
    components: {
      mgMiniPagination,
      mgTableColumn,
      mgColumnConfig,
    },
    data() {
      return {
        pagination: {
          page: this.apiParams?.page || 1,
          size: this.apiParams?.size || 20,
        },
        visible: false,
        total: 0,
        list: [],
        loading: false,
        // 是否显示表头搜索
        showSearchBar: false,
        // 高亮行
        hightLightRows: [],
        // 表头搜索参数
        searchParams: {},
      }
    },
    computed: {
      page() {
        return this.pagination.page
      },
      totalPage() {
        return Math.ceil(this.total / this.pagination.size)
      },
      height() {
        // 此处表格的高度应在渲染之前就给固定值，否则要需要调用一次 doLayout() 方法造成不必要的计算开销
        // topbar: 38px  pagination: 32px  偏移量 23px
        if (this.showPagination) return 'calc(100% - 38px - 32px - 12px)'
        return 'calc(100% - 38px)'
      },
      elTable() {
        return this.$refs.mgTable
      },
      headerCellClassName() {
        return this.showSearchBar ? 'is-searchable' : null
      },
      showTitle() {
        return !!this.title
      },
      resolvedTableBorder() {
        if (this.$attrs.border !== undefined) return this.$attrs.border
        // 保留 border 能力，避免影响列宽拖拽命中区域
        return true
      },
      tableLineClass() {
        return this.tableLineMode === 'horizontal' ? 'mg-table--line-horizontal' : ''
      },
    },
    created() {
      if (this.api && this.autoLoad) {
        this.loadData()
      }
      if (this.searchable) {
        this.setSearchParams()
      }
    },
    methods: {
      handleShowColumnConfig() {
        this.visible = true
      },
      setHighlightRows(rows) {
        this.hightLightRows = rows
      },
      // 行点击（设置高亮）
      handleRowClick(row, column, event) {
        const index = this.hightLightRows.findIndex((item) => item === row)
        index == -1 ? this.hightLightRows.push(row) : this.hightLightRows.splice(index, 1)
        this.$emit('row-click', row, column, event)
      },
      getRowClassName({ row }) {
        return this.hightLightRows.find((item) => item === row) ? 'current-row' : ''
      },
      getColumnClassName(column) {
        return column.headerModel ? 'el-table_searchable_column' : column.className
      },
      getIndex(index) {
        return index + 1 + (this.page - 1) * this.pagination.size
      },
      // 初始化表头搜索参数
      setSearchParams() {
        this.columns.forEach((col) => {
          if (col.headerModel && col.headerModel !== 'index') {
            this.$set(this.searchParams, col.headerModel, null)
          }
        })
      },
      // 表头搜索
      handleSearch() {
        this.handleCurrentChange(1)
      },
      // 重置搜索
      handleReset() {
        let isNeedSearch = false
        for (const key in this.searchParams) {
          if (this.searchParams[key]) {
            this.searchParams[key] = null
            isNeedSearch = true
          }
        }
        if (isNeedSearch) {
          this.handleSearch()
        }
      },
      // 显示/隐藏 表头搜索栏
      handleToggleSearchBar() {
        this.showSearchBar = !this.showSearchBar
        this.$emit('searchable-change', this.showSearchBar)
        if (!this.showSearchBar) {
          this.handleReset()
        }
        this.updateLayout(true)
      },
      // 翻页
      handleCurrentChange(page) {
        if (page) {
          this.pagination.page = page
        }
        this.loadData()
      },
      // 改变每页显示条数
      handleSizeChange(size) {
        this.pagination.page = 1
        this.pagination.size = size
        this.loadData()
      },
      // 更新表格布局
      updateLayout(shouldUpdateLayout = false) {
        if (this.$attrs['show-summary'] || shouldUpdateLayout) {
          this.$nextTick(() => {
            this.elTable.layout.updateElsHeight()
          })
        }
      },
      // 表格合计
      handleSummary({ columns, data }) {
        // 以用户传入的合计方法为主，否则使用 summary-option 的配置进行合计
        if (this.$attrs['summary-method'] && typeof this.$attrs['summary-method'] === 'function') {
          return this.$attrs['summary-method']({ columns, data })
        }
        const sums = [this.$attrs['sum-text'] || this.$MGUI.i18n.t('合计')]
        if (this.summaryOption) {
          columns.forEach((column, index) => {
            if (index !== 0) {
              sums[index] = this.summaryOption[column.property] || ''
            }
          })
        }
        return sums
      },
      getElTable() {
        return this.elTable
      },
      loadData() {
        this.loading = true
        this.$emit('loading-change', true)
        return this.api({
          ...this.apiParams,
          ...this.pagination,
          ...this.searchParams,
        })
          .then((res) => {
            this.total = res.data?.total || 0
            return typeof this.formatter === 'function' ? this.formatter(res) : res
          })
          .then((res) => {
            this.hightLightRows = []
            this.list = res.data?.items || []
          })
          .finally(() => {
            this.loading = false
            this.$emit('loading-change', false)
            this.updateLayout()
          })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .mg-table {
    flex: 1;
    box-sizing: border-box;
    border-radius: 4px;
    background-color: #fff;
    overflow: hidden;
    position: relative;
    padding-bottom: 10px;
    &__topbar {
      --mg-op-btn-height: 2.5rem;
      --mg-op-icon-size: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0;
      padding: 8px 8px 8px 0;
      .topbar-left,
      .topbar-right {
        display: flex;
        align-items: center;
      }
      .topbar-left {
        margin-right: 8px;
        height: var(--mg-op-btn-height);
      }
      .topbar-right {
        flex: 1;
        justify-content: flex-end;
        height: var(--mg-op-btn-height);
      }
      // 右侧操作区保持右对齐，不再将导出按钮吸附到左侧
      ::v-deep .topbar-right .mg-export-button {
        margin-right: 0;
        margin-left: 0;
        order: 0;
      }
      .topbar-left {
        strong {
          font-size: 14px;
          margin-right: 10px;
        }
      }
      ::v-deep .topbar-left .mg-export-button,
      ::v-deep .topbar-right .mg-export-button {
        height: var(--mg-op-btn-height);
        min-height: var(--mg-op-btn-height);
        box-sizing: border-box;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      ::v-deep .topbar-left .el-button:not(.el-button--icon):not(.el-button--text):not(.mg-export-button),
      ::v-deep .topbar-right .el-button:not(.el-button--icon):not(.el-button--text):not(.mg-export-button) {
        height: var(--mg-op-btn-height);
        min-height: var(--mg-op-btn-height);
        line-height: var(--mg-op-btn-height);
        box-sizing: border-box;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      ::v-deep .topbar-left .el-button:not(.el-button--icon):not(.el-button--text):not(.mg-export-button) {
        font-size: 18px;
        font-style: normal;
      }
      ::v-deep .topbar-left .el-button--icon,
      ::v-deep .topbar-right .el-button--icon,
      ::v-deep .topbar-left .mg-setting-icon-btn,
      ::v-deep .topbar-right .mg-setting-icon-btn {
        width: var(--mg-op-icon-size);
        min-width: var(--mg-op-icon-size);
        height: var(--mg-op-icon-size);
        min-height: var(--mg-op-icon-size);
        box-sizing: border-box;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }
      ::v-deep .topbar-left .el-button--icon,
      ::v-deep .topbar-right .el-button--icon,
      ::v-deep .topbar-left .mg-export-button.is-icon-type,
      ::v-deep .topbar-right .mg-export-button.is-icon-type,
      ::v-deep .topbar-left .el-button--icon:hover,
      ::v-deep .topbar-right .el-button--icon:hover,
      ::v-deep .topbar-left .el-button--icon:focus,
      ::v-deep .topbar-right .el-button--icon:focus,
      ::v-deep .topbar-left .el-button--icon:active,
      ::v-deep .topbar-right .el-button--icon:active,
      ::v-deep .topbar-left .el-button--icon.is-disabled,
      ::v-deep .topbar-right .el-button--icon.is-disabled,
      ::v-deep .topbar-left .mg-export-button.is-icon-type.is-disabled,
      ::v-deep .topbar-right .mg-export-button.is-icon-type.is-disabled {
        border-color: #d2d3d7 !important;
      }
      ::v-deep .topbar-left .el-button--icon .iconfont,
      ::v-deep .topbar-right .el-button--icon .iconfont {
        vertical-align: 0;
      }
      ::v-deep .topbar-left .mg-setting-icon-btn,
      ::v-deep .topbar-right .mg-setting-icon-btn {
        font-size: 0;
        line-height: 0;
      }
      ::v-deep .topbar-left .mg-setting-icon-btn .mg-setting-icon-img,
      ::v-deep .topbar-right .mg-setting-icon-btn .mg-setting-icon-img {
        display: block;
        margin: auto;
      }
    }
    .mg-pagination {
      text-align: center;
      margin-top: 10px;
    }
    ::v-deep .mg-custom-thead-content .mg-header-search-reset-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 40px;
      min-height: 40px;
      box-sizing: border-box;
      padding-left: 2px;
      padding-top: 2px;
      background-color: transparent !important;
    }
    ::v-deep .mg-custom-thead-content .mg-header-search-reset-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 40px;
      min-height: 40px;
      line-height: 40px;
      margin: 0;
      padding: 0;
      border: none !important;
      background-color: transparent !important;
    }
    ::v-deep .mg-custom-thead-content .mg-header-search-reset-btn:hover,
    ::v-deep .mg-custom-thead-content .mg-header-search-reset-btn:focus,
    ::v-deep .mg-custom-thead-content .mg-header-search-reset-btn:active {
      border: none !important;
      background-color: transparent !important;
      box-shadow: none !important;
    }

    ::v-deep .mg-setting-icon-btn,
    ::v-deep .mg-setting-icon-btn:hover,
    ::v-deep .mg-setting-icon-btn:focus {
      border-color: #d2d3d7;
    }
    ::v-deep .mg-setting-icon-btn .mg-setting-icon-img {
      width: 20px;
      height: 20px;
      display: inline-block;
      background: url('@/assets/common/icon_set_new.png') no-repeat center;
      background-size: contain;
      flex-shrink: 0;
    }
    // 显示合计时，去除底部多余的横线
    &[show-summary] {
      .el-table::before {
        height: 0;
      }
    }

    &.mg-table--line-horizontal {
      ::v-deep .el-table,
      ::v-deep .el-table__inner-wrapper,
      ::v-deep .el-table__fixed,
      ::v-deep .el-table__fixed-right,
      ::v-deep .el-table__fixed-left {
        border: none !important;
      }

      ::v-deep .el-table::before,
      ::v-deep .el-table--border::after,
      ::v-deep .el-table--group::after,
      ::v-deep .el-table__inner-wrapper::before {
        display: none !important;
        height: 0 !important;
      }

      ::v-deep .el-table th.el-table__cell,
      ::v-deep .el-table td.el-table__cell {
        // 保留透明右边框占位，避免列宽拖拽命中区域丢失
        border-right: 1px solid transparent !important;
        border-bottom: 1px solid #f2f2f2 !important;
      }
    }
  }
</style>
